// Dates as 'YYYY-MM-DD' strings and months as 'YYYY-MM' strings: they sort and compare correctly as text,
// and never shift with time zones.

const pad = (n) => String(n).padStart(2, '0');
export const toDate = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
export const toMonth = (y, m) => `${y}-${pad(m)}`;
export const parts = (s) => s.split('-').map(Number);
const utcOf = (s) => { const [y, m, d] = parts(s); return Date.UTC(y, m - 1, d); };
const fromUtc = (ms) => { const d = new Date(ms); return toDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()); };
export const addDays = (s, n) => fromUtc(utcOf(s) + n * 864e5);
export const weekday = (s) => new Date(utcOf(s)).getUTCDay(); // 0 = Sunday
export const daysBetween = (a, b) => Math.round((utcOf(b) - utcOf(a)) / 864e5);
export const daysInMonth = (monthKey) => { const [y, m] = parts(monthKey); return new Date(Date.UTC(y, m, 0)).getUTCDate(); };
export const daysInYear = (y) => ((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 366 : 365);

export const isMonth = (s) => typeof s === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(s);
export const isDate = (s) => {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = parts(s);
  return m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(toMonth(y, m));
};
export const monthOf = (date) => date.slice(0, 7);
export const yearOf = (key) => Number(key.slice(0, 4));
export const monthNum = (key) => Number(key.slice(5, 7));
export function addMonths(key, n) {
  const [y, m] = parts(key);
  const i = y * 12 + (m - 1) + n;
  return toMonth(Math.floor(i / 12), (i % 12) + 1);
}
export const monthsBetween = (a, b) => { const [ya, ma] = parts(a); const [yb, mb] = parts(b); return (yb - ya) * 12 + (mb - ma); };
export function monthRange(from, to) {
  const out = [];
  if (!isMonth(from) || !isMonth(to) || to < from) return out;
  for (let k = from; k <= to && out.length < 1200; k = addMonths(k, 1)) out.push(k);
  return out;
}
export const firstDay = (key) => `${key}-01`;
export const lastDay = (key) => `${key}-${pad(daysInMonth(key))}`;

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const monthName = (m) => MONTH_NAMES[m - 1];
export const monthLabel = (key, { short = false } = {}) => {
  const name = monthName(monthNum(key));
  return `${short ? name.slice(0, 3) : name} ${yearOf(key)}`;
};
export const dateLabel = (date, { weekday: wd = true } = {}) => {
  const [y, m, d] = parts(date);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return `${wd ? `${days[weekday(date)]} ` : ''}${d} ${monthName(m).slice(0, 3)} ${y}`;
};

// Today's date where the money lives (Lisbon), whatever the device's time zone
export function todayIn(tz = 'Europe/Lisbon', now = Date.now()) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(now));
  } catch {
    return fromUtc(now);
  }
}

/* ── Portuguese public holidays ───────────────────────── */

// Easter Sunday (Gregorian calendar, anonymous algorithm)
export function easter(y) {
  const a = y % 19;
  const b = Math.floor(y / 100);
  const c = y % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return toDate(y, month, day);
}

// National holidays, plus Carnival and a municipal holiday when the person gets them.
// extra: ['MM-DD', ...] repeated every year, or full 'YYYY-MM-DD' dates.
export function holidays(y, { carnival = false, extra = [] } = {}) {
  const e = easter(y);
  const list = [
    [`${y}-01-01`, 'New Year'],
    [addDays(e, -2), 'Good Friday'],
    [e, 'Easter'],
    [`${y}-04-25`, 'Freedom Day'],
    [`${y}-05-01`, 'Labour Day'],
    [addDays(e, 60), 'Corpus Christi'],
    [`${y}-06-10`, 'Portugal Day'],
    [`${y}-08-15`, 'Assumption'],
    [`${y}-10-05`, 'Republic Day'],
    [`${y}-11-01`, "All Saints' Day"],
    [`${y}-12-01`, 'Restoration of Independence'],
    [`${y}-12-08`, 'Immaculate Conception'],
    [`${y}-12-25`, 'Christmas'],
  ];
  if (carnival) list.push([addDays(e, -47), 'Carnival']);
  for (const x of extra || []) {
    if (/^\d{2}-\d{2}$/.test(x) && isDate(`${y}-${x}`)) list.push([`${y}-${x}`, 'Local holiday']);
    else if (isDate(x) && x.startsWith(`${y}-`)) list.push([x, 'Day off']);
  }
  return new Map(list);
}

const holidayCache = new Map();
function holidaySet(y, opts) {
  const key = `${y}|${opts.carnival ? 1 : 0}|${(opts.extra || []).join(',')}`;
  if (!holidayCache.has(key)) {
    if (holidayCache.size > 500) holidayCache.clear();
    holidayCache.set(key, new Set(holidays(y, opts).keys()));
  }
  return holidayCache.get(key);
}

export const isWeekend = (date) => { const w = weekday(date); return w === 0 || w === 6; };
export const isBusinessDay = (date, opts = {}) => !isWeekend(date) && !holidaySet(parts(date)[0], opts).has(date);

// Working days (Mon–Fri, not holidays) in a month, optionally only from/until certain dates
export function businessDays(monthKey, opts = {}, { from = null, until = null } = {}) {
  const out = [];
  const n = daysInMonth(monthKey);
  for (let d = 1; d <= n; d++) {
    const date = `${monthKey}-${pad(d)}`;
    if (from && date < from) continue;
    if (until && date > until) continue;
    if (isBusinessDay(date, opts)) out.push(date);
  }
  return out;
}

/* ── Paydays ──────────────────────────────────────────────
   rule: { type: 'last-business-day' } | { type: 'day', day: 1..31, shift: 'before' | 'after' | 'none' }
   A fixed day that falls on a weekend or holiday moves to the previous (or next) working day. */
export function payday(monthKey, rule = { type: 'last-business-day' }, opts = {}) {
  if (!rule || rule.type === 'last-business-day') {
    let d = lastDay(monthKey);
    for (let i = 0; i < 31 && !isBusinessDay(d, opts); i++) d = addDays(d, -1);
    return d;
  }
  if (rule.type === 'first-business-day-next') {
    let d = firstDay(addMonths(monthKey, 1));
    for (let i = 0; i < 31 && !isBusinessDay(d, opts); i++) d = addDays(d, 1);
    return d;
  }
  const inMonth = rule.nextMonth ? addMonths(monthKey, 1) : monthKey;
  const day = Math.min(Math.max(1, Math.round(Number(rule.day) || 1)), daysInMonth(inMonth));
  let d = `${inMonth}-${pad(day)}`;
  if (rule.shift === 'before') for (let i = 0; i < 31 && !isBusinessDay(d, opts); i++) d = addDays(d, -1);
  if (rule.shift === 'after') for (let i = 0; i < 31 && !isBusinessDay(d, opts); i++) d = addDays(d, 1);
  return d;
}

export function paydayRuleLabel(rule) {
  if (!rule || rule.type === 'last-business-day') return 'Last working day of the month';
  if (rule.type === 'first-business-day-next') return 'First working day of the next month';
  const shift = rule.shift === 'before' ? ' (earlier if not a working day)' : rule.shift === 'after' ? ' (later if not a working day)' : '';
  return `Day ${rule.day} of ${rule.nextMonth ? 'the following month' : 'the same month'}${shift}`;
}

// Age on a date, from a birth date
export function ageOn(birthDate, date) {
  if (!isDate(birthDate) || !isDate(date)) return null;
  const [by, bm, bd] = parts(birthDate);
  const [y, m, d] = parts(date);
  return y - by - (m < bm || (m === bm && d < bd) ? 1 : 0);
}
